import {
    CheckCircledIcon,
    UpdateIcon
} from '@radix-ui/react-icons';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import '../App.css';
import { DatePickerSingle } from '../components/datePickerSingle';
import SelectBunnyPopover from '../components/selectBunnyPopver';
import { Button } from '../components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import api from '../lib/api';

function AddBunny() {

    const form = useForm();
    const { setValue } = useForm();
    const { auth } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        birthDate: '',
        gender: '',
        color: '',
        father: null,
        mother: null,
        straightClassId: 1,
        crookedClassId: 7,
        heightClassId: 13,
        lengthClassId: 16,
        litterId: '',
    });
    const [searchParams] = useSearchParams();
    const litterIdFromParams = searchParams.get('litterId');
    const birthDateFromParams = searchParams.get('birthDate');
    const fatherFromParams = searchParams.get('fatherId');
    const motherFromParams = searchParams.get('motherId');

    const [isLitterIdFromParams, setIsLitterIdFromParams] = useState(false);
    const [isBirthDateFromParams, setIsBirthDateFromParams] = useState(false);
    const [isFatherFromParams, setIsFatherFromParams] = useState(false);
    const [isMotherFromParams, setIsMotherFromParams] = useState(false);

    const navigate = useNavigate();

    // Second useEffect to handle setting the form values from params after loading
    useEffect(() => {
        if (!isLoading) {
            if (litterIdFromParams) {
                setFormData(prevData => ({
                    ...prevData,
                    litterId: parseInt(litterIdFromParams)
                }));
                setValue('litterId', litterIdFromParams);  // Ensure 'litterId' matches the field name
                setIsLitterIdFromParams(true);
            }

            if (birthDateFromParams) {
                setFormData(prevData => ({
                    ...prevData,
                    birthDate: birthDateFromParams,
                }));
                setValue('birthDate', birthDateFromParams);  // Ensure 'birthDate' matches the field name
                setIsBirthDateFromParams(true);
            }

            if (fatherFromParams) {
                setFormData(prevData => ({
                    ...prevData,
                    father: fatherFromParams,
                }));
                setValue('father', fatherFromParams);  // Ensure 'father' matches the field name
                setIsFatherFromParams(true);
            }

            if (motherFromParams) {
                setFormData(prevData => ({
                    ...prevData,
                    mother: motherFromParams,
                }));
                setValue('mother', motherFromParams);  // Ensure 'mother' matches the field name
                setIsMotherFromParams(true);
            }
        }
    }, [isLoading, litterIdFromParams, birthDateFromParams, fatherFromParams, motherFromParams, setValue]);

    const createBunny = (e) => {

        if (isLoading) return;

        e.preventDefault();
        setIsLoading(true);

        api.post(`https://skhrf-api.cmdigital.se/v1/bunnies/create`, {
            owner: auth.user.id,
            ...formData,
        }).then(response => {

            setFormData({
                name: '',
                gender: '',
                color: '',
                straightClassId: 1, // mini
                crookedClassId: 7, // mini
                heightClassId: 13, // ej elit
                lengthClassId: 16, // ej elit
                ...(isLitterIdFromParams ? {} : { litterId: '' }), // Reset litterId only if it was set by the user
                ...(isBirthDateFromParams ? {} : { birthDate: null }), // Reset birthDate only if it was set by the user
            });
            setIsLoading(false);
            toast({
                variant: 'success',
                title: 'Ansökan skapad!',
                // description: "Informationen uppdaterades utan problem.",
            });
            if (!litterIdFromParams) return navigate('/mybunnies');
        }).catch(error => {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
            setIsLoading(false);
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        setValue(name, value);
    };

    const handleClassChange = (name, value) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: parseInt(value),
        }));
    };

    const handleDateChange = (date) => {
        if (!date) {

            return; // Early return if the date is null or undefined
        }

        const formattedDate = convertToUTC(date);

        setFormData(prevState => ({
            ...prevState,
            birthDate: formattedDate
        }));
    };

    const convertToUTC = (date) => {
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
    };

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <h1 className='font-medium text-2xl mb-2'>
                    {litterIdFromParams ? 'Ansök om att lägga till kanin i kull' : 'Ansök om att lägga till kanin'}
                </h1>
                <Form {...form} >
                    <form className="flex flex-col gap-6 w-full sm:w-1/2">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Namn</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Johan" name="name" value={formData.name} onChange={handleChange} disabled={isLoading} autoComplete='off' />
                                    </FormControl>
                                    <FormDescription>
                                        Endast kaninens namn. Gårdsnamn läggs till automatiskt.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {!isBirthDateFromParams && (
                            <FormField
                                control={form.control}
                                name="birthDate"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel htmlFor='birthDate'>Födelsedatum</FormLabel>
                                        <FormControl>
                                            <DatePickerSingle
                                                id='birthDate'
                                                onDateChange={handleDateChange}
                                                selectedDate={formData.birthDate ? new Date(formData.birthDate) : null}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                        <FormField
                            control={form.control}
                            name="gender"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel asChild><legend>Kön</legend></FormLabel>
                                    <FormControl>
                                        <RadioGroup value={formData.gender}
                                            onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="male" id="male" />
                                                <Label htmlFor="male">Hane</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="female" id="female" />
                                                <Label htmlFor="female">Hona</Label>
                                            </div>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='grid grid-cols-2 gap-6'>
                            <FormField
                                control={form.control}
                                name="straightClassId"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel asChild><legend>Nivå i rak</legend></FormLabel>
                                        <FormControl>
                                            <RadioGroup
                                                value={formData.straightClassId}
                                                onValueChange={(value) => handleClassChange('straightClassId', value)}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value={1} id="1" />
                                                    <Label htmlFor="1">Mini</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value={2} id="2" />
                                                    <Label htmlFor="2">Lätt</Label>
                                                </div>
                                            </RadioGroup>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="crookedClassId"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel asChild><legend>Nivå i krok</legend></FormLabel>
                                        <FormControl>
                                            <RadioGroup
                                                value={formData.crookedClassId}
                                                onValueChange={(value) => handleClassChange('crookedClassId', value)}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value={7} id="7" />
                                                    <Label htmlFor="7">Mini</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value={8} id="8" />
                                                    <Label htmlFor="8">Lätt</Label>
                                                </div>
                                            </RadioGroup>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="heightClassId"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel asChild><legend>Nivå i höjd</legend></FormLabel>
                                        <FormControl>
                                            <RadioGroup
                                                value={formData.heightClassId}
                                                onValueChange={(value) => handleClassChange('heightClassId', value)}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value={13} id="13" />
                                                    <Label htmlFor="13">Ej elit</Label>
                                                </div>
                                                {/* <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value={14} id="elite" />
                                                    <Label htmlFor="elite">Elit</Label>
                                                </div> */}
                                            </RadioGroup>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="lengthClassId"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel asChild><legend>Nivå i längd</legend></FormLabel>
                                        <FormControl>
                                            <RadioGroup
                                                value={formData.lengthClassId}
                                                onValueChange={(value) => handleClassChange('lengthClassId', value)}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value={16} id="16" />
                                                    <Label htmlFor="16">Ej elit</Label>
                                                </div>
                                                {/* <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value={17} id="elite" />
                                                    <Label htmlFor="elite">Elit</Label>
                                                </div> */}
                                            </RadioGroup>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <FormField
                            control={form.control}
                            name="color"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Färg</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Madagaskar" name="color" value={formData.color} onChange={handleChange} disabled={isLoading} autoComplete='off' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {!isFatherFromParams && (
                            <SelectBunnyPopover
                                form={form}
                                name="father"
                                label="Far"
                                gender="male"
                                onSelect={(bunnyId) => setFormData(prevState => ({ ...prevState, father: bunnyId }))}
                                allowDead={true}
                            />
                        )}
                        {!isMotherFromParams && (
                            <SelectBunnyPopover
                                form={form}
                                name="mother"
                                label="Mor"
                                gender="female"
                                onSelect={(bunnyId) => setFormData(prevState => ({ ...prevState, mother: bunnyId }))}
                                allowDead={true}
                            />
                        )}
                        {(isBirthDateFromParams || isFatherFromParams || isMotherFromParams) && (
                            <p className="text-gray-600 text-sm">
                                Födelsedatum, far och mor har automatiskt fyllts i från kullinformationen.
                            </p>
                        )}
                        <Button type="submit" disabled={isLoading} onClick={createBunny}>
                            {isLoading ? <UpdateIcon className='loader-spinner' /> : <><CheckCircledIcon className='mr-3' />Ansök</>}
                        </Button>
                    </form>
                </Form>
            </div>
        </div>
    );
}

export default AddBunny;